/**
 * Scroll intro: section headings, groups of items and large photos fade in the first
 * time they scroll into view (items one after another, photos settle into place).
 *
 * Progressive enhancement: nothing is hidden until this script tags it, anything
 * already on screen is left alone, and it does nothing under reduced motion or in
 * the theme editor. Loaded from layout/theme.liquid when settings.sn_scroll_reveal
 * is on; styles live in assets/safinah-base.css (.sn-reveal).
 */
(() => {
  if (window.Shopify?.designMode) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (!('IntersectionObserver' in window)) return;

  const HEADINGS = [
    '.sn-heading__inner',
    '.sn-shelf__header',
    '.sn-quotes__header',
    '.sn-articles__header',
    '.sn-blog__more-head',
    '.sn-article-related__head',
    '.sn-scholar__others-title',
    '.sn-footer__news-inner',
  ];
  const ITEMS = [
    '.sn-shelf__item',
    '.sn-articles__grid > *',
    '.sn-blog-grid__item',
    '.sn-article-related__item',
    '.sn-scholar__grid > *',
    '.sn-scholar__others-list > *',
    '.product-grid__item',
    '.section-content.layout-panel-flex > .group-block',
    '.sn-article-promo__card',
    '.sn-footer__main > *',
  ];
  // Containers first, so photos inside an already tagged container are skipped.
  const MEDIA = ['featured-product-information .product-information__media', '.product-information__media .product-media', '.sn-article__figure'];

  const STAGGER_MS = 90;
  const MAX_STEPS = 5;
  const DURATION_MS = { heading: 800, item: 800, media: 1100 };

  /** Elements whose own motion must not be touched: buy box and pinned rows. */
  const isExcluded = (el) =>
    el.closest('.product-details, .buy-buttons-block, .sn-quotes__track, sn-scroll-pin[data-pinned] [data-pin-track]');

  const onScreen = (rect) => rect.top < window.innerHeight * 0.92 && rect.bottom > 0;

  /** A swipeable row: its off-screen items would never intersect, so it reveals as one. */
  const scrollRow = (el) => {
    const parent = el.parentElement;
    if (!parent) return null;
    const { overflowX } = getComputedStyle(parent);
    return (overflowX === 'auto' || overflowX === 'scroll') && parent.scrollWidth > parent.clientWidth + 1 ? parent : null;
  };

  /** @param {Element} el @param {'heading' | 'item' | 'media'} kind @returns {Element | false} the element to observe */
  const tag = (el, kind) => {
    const row = kind === 'item' ? scrollRow(el) : null;
    if (row) return tag(row, 'heading') && row;
    if (el.closest('.sn-reveal') || el.querySelector('.sn-reveal') || isExcluded(el)) return false;
    const rect = el.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0 || onScreen(rect)) return false;
    el.classList.add('sn-reveal', `sn-reveal--${kind}`);
    el.dataset.snReveal = kind;
    return el;
  };

  const reveal = (el, delay) => {
    el.style.setProperty('--sn-reveal-delay', `${delay}ms`);
    el.classList.add('is-revealed');
    // Hand the element back to its own styles once the intro has played.
    const done = delay + DURATION_MS[el.dataset.snReveal] + 100;
    setTimeout(() => {
      el.classList.remove('sn-reveal', 'sn-reveal--heading', 'sn-reveal--item', 'sn-reveal--media', 'is-revealed');
      el.style.removeProperty('--sn-reveal-delay');
      delete el.dataset.snReveal;
    }, done);
  };

  const observer = new IntersectionObserver(
    (entries) => {
      const rtl = document.documentElement.dir === 'rtl';
      const shown = entries
        .filter((entry) => entry.isIntersecting)
        .map((entry) => ({ el: entry.target, rect: entry.boundingClientRect }))
        .sort((a, b) => a.rect.top - b.rect.top || (rtl ? b.rect.right - a.rect.right : a.rect.left - b.rect.left));

      const hasHeading = shown.some(({ el }) => el.dataset.snReveal === 'heading');
      let step = 0;
      for (const { el } of shown) {
        observer.unobserve(el);
        if (el.dataset.snReveal === 'heading') {
          reveal(el, 0);
        } else {
          reveal(el, (hasHeading ? 150 : 0) + Math.min(step, MAX_STEPS) * STAGGER_MS);
          step += 1;
        }
      }
    },
    { rootMargin: '0px 0px -8% 0px' }
  );

  const init = () => {
    /** @type {Array<[string[], 'heading' | 'item' | 'media']>} */
    const groups = [
      [MEDIA, 'media'],
      [HEADINGS, 'heading'],
      [ITEMS, 'item'],
    ];
    for (const [selectors, kind] of groups) {
      for (const el of document.querySelectorAll(selectors.join(','))) {
        const tagged = tag(el, kind);
        if (tagged) observer.observe(tagged);
      }
    }
  };

  // Let custom elements (e.g. <sn-scroll-pin>) settle their layout first.
  const start = () => requestAnimationFrame(init);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
